import { FilterAction, FilterState, Filters } from '../../reducer'
import FilterOverlay from './FilterOverlay'
import * as S from './elements'
import { FloatingServiceMenu } from '@components/Page'
import { Box, Icon, breakpoints } from '@gassan-ui'
import { ArticleAttributeFilter, BrandCollectionFilter, BrandTypeFilter } from '@generated'
import { useBodyScrollLock } from '@lib/hooks/useBodyScrollLock'
import Portal from '@reach/portal'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import React, { FC, useEffect, useRef, useState } from 'react'
import SortBackdrop from '@components/SortBackdrop'

type Props = FilterState & {
  isRolexRelevantPage?: boolean
  dispatch: React.Dispatch<FilterAction>
  fixedFilters: Filters
  attributes: ArticleAttributeFilter[]
  brandOptions: BrandTypeFilter[]
  collectionOptions: BrandCollectionFilter[]
  isPreOwned: boolean
}

const MobileFilters: FC<Props> = ({
  isRolexRelevantPage = false,
  sort,
  dispatch,
  fixedFilters,
  attributes,
  brandOptions,
  collectionOptions,
  isPreOwned,
  ...state
}) => {
  const [active, setActive] = useState<'filter' | 'sort' | null>(null)
  const { t } = useTranslation('other')
  const [shouldRender, setShouldRender] = useState(false)
  const ref = useRef<HTMLDivElement>()

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints.large})`)

    function handleMediaQueryChange(mql) {
      setShouldRender(!mql.matches)
    }

    handleMediaQueryChange(mediaQuery)
    mediaQuery.addListener(handleMediaQueryChange)
    return () => {
      mediaQuery.removeListener(handleMediaQueryChange)
    }
  }, [])

  const isActive = active !== null
  useBodyScrollLock(ref, isActive)

  if (!shouldRender) {
    return <FloatingServiceMenu />
  }

  let total = Object.keys(state.filters).reduce((prev, key) => {
    const slice = state.filters[key]
    if (!fixedFilters[parseInt(key, 10)]) {
      return prev + slice.length
    }
    return prev
  }, 0)
  total = total + state.brands.length + state.collections.length

  return (
    <>
      <Portal>
        <AnimatePresence>
          <S.Grid
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.1 }}
            key="buttons"
          >
            <S.Col>
              <S.Button
                onClick={() => setActive(active === 'filter' ? null : 'filter')}
                isActive={active === 'filter'}
              >
                Filter
                {total > 0 ? (
                  <S.Indicator>{total}</S.Indicator>
                ) : (
                  <Icon icon={active === 'filter' ? 'settings-alt' : 'settings'} />
                )}
              </S.Button>
            </S.Col>
            <Box backgroundColor="shades.200"></Box>
            <S.Col>
              <S.Button
                onClick={() => setActive(active === 'sort' ? null : 'sort')}
                isActive={active === 'sort'}
              >
                {t('sort')}
                <Icon
                  icon="chevron-down-small"
                  style={{ transform: `rotate(${active === 'sort' ? '180deg' : '0'})` }}
                />
              </S.Button>
            </S.Col>
          </S.Grid>

          {active === 'sort' && (
            <SortBackdrop
              sort={sort}
              onSortChange={(sort) =>
                dispatch({
                  type: 'set-sort',
                  sort,
                })
              }
              onRequestClose={() => setActive(null)}
              sortOptions={[
                { label: t('popularity', { ns: 'filters' }), value: 'populair' },
                { label: t('newestFirst', { ns: 'filters' }), value: 'new' },
                { label: t('priceASC', { ns: 'filters' }), value: 'priceASC' },
                { label: t('priceDESC', { ns: 'filters' }), value: 'priceDESC' },
              ]}
            />
          )}
          {active === 'filter' && (
            <FilterOverlay
              showRolexLink={isRolexRelevantPage}
              dispatch={dispatch}
              onRequestClose={() => setActive(null)}
              attributes={attributes}
              fixedFilters={fixedFilters}
              ref={ref}
              brandOptions={brandOptions}
              collectionOptions={collectionOptions}
              totalAppliedFitlers={total}
              isPreOwned={isPreOwned}
              {...state}
            />
          )}
        </AnimatePresence>
      </Portal>
      {active === null && <FloatingServiceMenu variant="move-element-up" />}
    </>
  )
}

export default MobileFilters
